import { useMemo, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { ArrowRightCircle, CalendarOff } from 'lucide-react'
import { toast } from 'sonner'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Board, BoardSkeleton } from '@/components/kanban/board'
import { IssueDataTable, TableSkeleton } from '@/components/tables/data-table'
import { ISSUE_COLUMNS } from '@/components/tables/issue-columns'
import { StageIcon } from '@/components/common/badges'
import { ViewModeToggle } from '@/components/common/view-mode-toggle'
import { useViewMode } from '@/hooks/use-view-mode'
import { ViewHeader } from '@/components/shell/view-header'
import { useOpenIssueSheet } from '@/hooks/use-ui-state'
import { useViewFilters } from '@/hooks/use-view-filters'
import { fieldByName, queryClient, useIssueFields, useIssues } from '@/lib/query'
import { ViewError } from '@/components/common/query-states'
import { refetchAfterBulk } from '@/lib/issue-write'
import { setFieldWrite, useSetIssueField } from '@/lib/mutations'
import { useContextOrder, useReorderContext } from '@/hooks/use-context-order'
import { useMe } from '@/hooks/use-me'
import { byManualOrder, EMPTY_ORDER } from '@/lib/context-order'
import { PRIORITY_ORDER, isLiveStage, priorityIndex, priorityVar } from '@/lib/stage'
import { SPRINT_VIEW, sprintStages as stagesForSprint } from '@/lib/views'
import { localToday } from '@/lib/local-date'
import {
  currentSprint,
  nextSprint,
  parseSprintName,
  previousSprint,
  releasedDuring,
  sprintDay,
  SPRINT_LENGTH_DAYS,
} from '@/lib/sprint'
import { liveReleasedAt, type WorkIssue } from '@/types/github'

export default function Sprint() {
  const { id } = useParams()
  const issuesQuery = useIssues()
  const { data: issues, isPending } = issuesQuery
  const fieldsQuery = useIssueFields()
  const fields = fieldsQuery.data
  const openIssue = useOpenIssueSheet()
  const chipFilter = useViewFilters()
  const me = useMe()
  const setField = useSetIssueField()
  const [searchParams, setSearchParams] = useSearchParams()
  const [view, setView] = useViewMode()

  const groupBy = searchParams.get('group') === 'Priority' ? 'Priority' : 'Stage'
  const sortParam = searchParams.get('sort')
  // "Manual" = the sprint's own per-context order (replaces the old "Rank").
  const sortBy = sortParam === 'Priority' || sortParam === 'Created' ? sortParam : 'Manual'

  const sprintOptions = useMemo(
    () => fieldByName(fields, 'Sprint')?.options.map((o) => o.name) ?? [],
    [fields],
  )

  const sprintStages = useMemo(
    () => stagesForSprint(fields),
    [fields],
  )

  const sprint = useMemo(() => {
    if (id) return parseSprintName(decodeURIComponent(id))
    return currentSprint(sprintOptions)
  }, [id, sprintOptions])

  // A separate manual-order file per (sprint, grouping) so the Stage and
  // Priority groupings don't share — and overwrite — each other's order.
  const slug = sprint ? sprint.name.replace(/[^A-Za-z0-9]+/g, '-').replace(/^-+|-+$/g, '') : null
  const context = slug ? `sprint/${slug}/${groupBy}` : null
  const order = useContextOrder(context).data ?? EMPTY_ORDER
  const reorder = useReorderContext(context ?? '')

  const upcoming = useMemo(() => nextSprint(sprintOptions), [sprintOptions])

  const velocity = useMemo(() => {
    const prior = previousSprint(sprintOptions)
    if (!prior || !issues) return null
    return issues.filter((i) => releasedDuring(prior, liveReleasedAt(i))).length
  }, [sprintOptions, issues])

  // Sprint = children only (team convention 2026-06-11): parents/epics carry
  // Quarter and live on the Roadmap; the sprint board is execution-only.
  const { itemsByColumn, hiddenParents, unfinished, offBoard } = useMemo(() => {
    if (!sprint) {
      return { itemsByColumn: new Map<string, WorkIssue[]>(), hiddenParents: 0, unfinished: [] as WorkIssue[], offBoard: 0 }
    }
    const tagged = (issues ?? []).filter((i) => i.sprint === sprint.name)
    const parents = tagged.filter((i) => i.subIssueCount > 0).length
    // "Done enough to leave the sprint": merged, closed, or live in Prod.
    // Released to QA is NOT done — Prod verification is what closes issues
    // (Verify view), so QA-gate work carries over.
    const isDone = (s: string | null) => s === 'Merged' || s === 'Done' || isLiveStage(s)
    const leftovers = tagged.filter((i) => i.subIssueCount === 0 && !isDone(i.stage))
    const pool = chipFilter.apply(SPRINT_VIEW.pool(issues ?? [], {
      currentSprintName: sprint.name, fields, sprintGroupBy: groupBy,
    }))
    const map = new Map<string, WorkIssue[]>()
    const columns = groupBy === 'Stage' ? sprintStages : [...PRIORITY_ORDER]
    for (const c of columns) map.set(c, [])
    // Children whose stage/priority has no column (e.g. a sprint-tagged Done
    // or Triage issue) aren't shown — count them rather than inflate the board.
    const off = chipFilter.apply(tagged.filter((i) => i.subIssueCount === 0)).length - pool.length
    for (const issue of pool) {
      const key = groupBy === 'Stage' ? (issue.stage ?? 'Ready') : (issue.priority ?? 'Backlog')
      map.get(key)!.push(issue)
    }
    for (const [col, list] of map) {
      if (sortBy === 'Manual') list.sort(byManualOrder(order.groups[col] ?? []))
      else if (sortBy === 'Priority')
        list.sort((a, b) => priorityIndex(a.priority) - priorityIndex(b.priority))
      else list.sort((a, b) => a.createdAt.localeCompare(b.createdAt))
    }
    return { itemsByColumn: map, hiddenParents: parents, unfinished: leftovers, offBoard: off }
  }, [issues, chipFilter, sprint, fields, groupBy, sortBy, sprintStages, order])

  // Reorder needs the Manual sort, a complete column cohort, AND reorder-team
  // membership (the sprint order is shared — server-enforced). The grouping
  // dimension is safe to chip-filter (it hides whole columns); any other active
  // chip hides within-column members → reorder against a partial set, so
  // fall back to sort-only.
  const safeChip = groupBy === 'Priority' ? 'priority' : null
  const cohortComplete = chipFilter.active.every((k) => k === safeChip)
  const canReorder = sortBy === 'Manual' && cohortComplete && !!me?.canReorderOthers

  const carryTarget = useMemo(() => {
    if (!sprint) return null
    return nextSprint(sprintOptions, new Date(sprint.end.getTime() - 1))
  }, [sprint, sprintOptions])

  const [confirmCarry, setConfirmCarry] = useState(false)

  if (fields && !sprint) {
    return (
      <div className="flex h-full flex-col">
        <ViewHeader title="Sprint" meta="No active sprint" chips={null} />
        <div className="text-muted-foreground m-auto flex flex-col items-center gap-3 p-10 text-center text-sm">
          <CalendarOff className="text-faint-foreground size-6" />
          {upcoming ? (
            <>
              <p>
                No active sprint — next is <strong>{upcoming.name}</strong>, starting{' '}
                {upcoming.start.toISOString().slice(0, 10)}.
              </p>
              <Button asChild size="sm" variant="outline">
                <Link to={`/sprint/${encodeURIComponent(upcoming.name)}`}>Jump to next sprint</Link>
              </Button>
            </>
          ) : (
            <>
              <p>
                No sprints configured. The <code className="font-mono">add-future-sprint</code>{' '}
                workflow in n3oltd/work maintains the rolling horizon — trigger it from GitHub
                Actions.
              </p>
              <Button asChild size="sm" variant="outline">
                <a
                  href="https://github.com/n3oltd/work/actions/workflows/add-future-sprint.yml"
                  target="_blank"
                  rel="noreferrer"
                >
                  Open workflow on GitHub
                </a>
              </Button>
            </>
          )}
        </div>
      </div>
    )
  }

  const total = [...itemsByColumn.values()].reduce((n, list) => n + list.length, 0)
  const day = sprint ? sprintDay(sprint) : 0
  const ended = sprint !== null && sprint.end <= localToday()

  const carryOver = async () => {
    if (!carryTarget || !sprint) return
    const target = carryTarget.name
    const from = sprint.name
    const results = await Promise.allSettled(
      unfinished.map((issue) =>
        setField.mutateAsync({ issue, fieldName: 'Sprint', value: target, silent: true }),
      ),
    )
    // Silent writes don't refetch; reconcile once for the whole batch.
    refetchAfterBulk(queryClient, setFieldWrite, results.map((r) => (r.status === 'fulfilled' ? r.value : undefined)))
    const moved = unfinished.filter((_, i) => results[i]!.status === 'fulfilled')
    const failed = results.length - moved.length
    if (moved.length === 0) {
      toast.error(`Couldn't carry over any issues to ${target}`)
      return
    }
    toast.success(
      `${moved.length} carried to ${target}${failed > 0 ? ` · ${failed} failed` : ''}`,
      {
        action: {
          label: 'Undo',
          onClick: async () => {
            const undone = await Promise.allSettled(
              moved.map((issue) => setField.mutateAsync({ issue, fieldName: 'Sprint', value: from, silent: true })),
            )
            refetchAfterBulk(queryClient, setFieldWrite, undone.map((r) => (r.status === 'fulfilled' ? r.value : undefined)))
          },
        },
      },
    )
  }

  const toolbar = (
    <div className="flex items-center gap-1.5">
      {ended && unfinished.length > 0 && carryTarget && (
        <Button
          size="sm"
          variant="outline"
          className="h-6 gap-1 px-2 text-[11px]"
          onClick={() => setConfirmCarry(true)}
        >
          <ArrowRightCircle className="size-3" />
          Carry over {unfinished.length} unfinished
        </Button>
      )}
      <ViewModeToggle mode={view} onChange={setView} />
      {/* Group/sort + drag-reorder are board-only; the table sorts via its
          own column headers. */}
      {view === 'board' && (
        <>
          <span className="text-faint-foreground">Group by</span>
          <Select
            value={groupBy}
            onValueChange={(v) =>
              setSearchParams((p) => {
                const next = new URLSearchParams(p)
                next.set('group', v)
                return next
              }, { replace: true })
            }
          >
            <SelectTrigger size="sm" className="h-6 w-24 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Stage">Stage</SelectItem>
              <SelectItem value="Priority">Priority</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-faint-foreground">Sort</span>
          <Select
            value={sortBy}
            onValueChange={(v) =>
              setSearchParams((p) => {
                const next = new URLSearchParams(p)
                next.set('sort', v)
                return next
              }, { replace: true })
            }
          >
            <SelectTrigger size="sm" className="h-6 w-24 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Manual">Manual</SelectItem>
              <SelectItem value="Priority">Priority</SelectItem>
              <SelectItem value="Created">Created</SelectItem>
            </SelectContent>
          </Select>
        </>
      )}
    </div>
  )

  const reorderHint =
    sortBy !== 'Manual'
      ? ''
      : !me?.canReorderOthers
        ? ' · planning team sets the order'
        : !cohortComplete
          ? ' · filtered (reorder paused)'
          : ''

  return (
    <div className="flex h-full flex-col">
      <ViewHeader
        title={sprint?.name ?? 'Sprint'}
        meta={
          sprint
            ? `${ended ? 'Ended · ' : `Day ${day} of ${SPRINT_LENGTH_DAYS} · `}${total} shown${
                velocity !== null ? ` · ${velocity} still-open releases last sprint` : ''
              }${offBoard > 0 ? ` · ${offBoard} off-board (Done/Triage)` : ''}${hiddenParents > 0 ? ` · ${hiddenParents} parent issue${hiddenParents === 1 ? '' : 's'} hidden (Sprint belongs on children)` : ''}${reorderHint}`
            : '…'
        }
        toolbar={toolbar}
      />
      <AlertDialog open={confirmCarry} onOpenChange={setConfirmCarry}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Carry {unfinished.length} unfinished to {carryTarget?.name}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Issues not yet Merged, live in Prod, or closed get re-tagged to the next sprint — anything at the QA gate counts as unfinished.
              The finished ones keep this sprint as their history.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={carryOver}>Carry over</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <div className="min-h-0 flex-1">
        {issuesQuery.isError || fieldsQuery.isError ? (
          <ViewError what="the sprint" error={issuesQuery.error ?? fieldsQuery.error} onRetry={() => { issuesQuery.refetch(); fieldsQuery.refetch() }} />
        ) : isPending || !sprint ? (
          view === 'table' ? <TableSkeleton /> : <BoardSkeleton columns={5} />
        ) : view === 'table' ? (
          <IssueDataTable
            issues={[...itemsByColumn.values()].flat()}
            columns={ISSUE_COLUMNS}
            sortable
            bulkKind="work"
            emptyTitle="No issues in this sprint"
          />
        ) : (
          <Board
            columns={
              groupBy === 'Stage'
                ? sprintStages.map((stage) => ({
                    key: stage,
                    title: stage,
                    glyph: <StageIcon stage={stage} />,
                  }))
                : PRIORITY_ORDER.map((p) => ({
                    key: p,
                    title: p,
                    glyph: (
                      <span
                        className="size-2 rounded-full"
                        style={{ background: `var(${priorityVar(p)})` }}
                        aria-hidden
                      />
                    ),
                  }))
            }
            itemsByColumn={itemsByColumn}
            onCardClick={openIssue}
            onMoveAcross={(issue, pos) => {
              setField.mutate({
                issue,
                fieldName: groupBy === 'Stage' ? 'Stage' : 'Priority',
                value: pos.toColumn,
              })
              // Also persist the drop position in the new column's order, else
              // the card lands at the unplaced tail instead of where it was
              // dropped.
              if (canReorder && context) reorder(pos.toColumn, pos.orderedIds)
            }}
            onReorder={
              canReorder && context
                ? (_issue, pos) => reorder(pos.toColumn, pos.orderedIds)
                : undefined
            }
            excludeContext={context ?? undefined}
          />
        )}
      </div>
    </div>
  )
}
